// ================================================
// Smart WebSocket Hook - Auto-upgrade from Anonymous to Authenticated
// Handles seamless connection upgrade based on user auth status
// ================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import Swal from 'sweetalert2';
import { queueApiRequest, REQUEST_PRIORITY } from '../services/ApiRequestManager';
import { withApiBaseUrl } from '../lib/apiBaseUrl';

const USER_PRESENCE_PING_MS = 45000;

interface ContentUpdateData {
    type: string;
    tenantId: string;
    content: any;
    contentType?: string;
    updatedBy?: string;
    timestamp: string;
}

interface MaintenanceSocketEvent {
    tenantId?: string | null;
    enabled?: boolean;
    reason?: string;
    estimatedEnd?: string | null;
    maintenanceStart?: string | null;
    countdownMinutes?: number;
}

interface ConfigUpdateData {
    type: string;
    tenantId: string;
    configType: string;
    data: any;
    metadata?: {
        updateType: string;
        updatedBy: string;
        affectedProviders?: string[];
        reason?: string;
    };
    timestamp: string;
    broadcast?: boolean;
    broadcastedAt?: string;
}

interface UseSmartWebSocketProps {
    tenantId: string;
    onContentUpdate: (content: any) => void;
    onConfigUpdate?: (configType: string, configData: any, metadata?: any) => void;
    enabled?: boolean;
}

interface TokenCache {
    token: string;
    expiresAt: number;
    tenantId: string;
}

// Auth Detection Utils
const getAuthToken = (): string | null => {
    try {
        // Check multiple possible token storage keys
        // authService stores as 'accessToken', but some code uses 'token' or 'authToken'
        return localStorage.getItem('accessToken') || 
               localStorage.getItem('token') || 
               localStorage.getItem('authToken') || 
               null;
    } catch (error) {
        console.warn('Failed to access localStorage:', error);
        return null;
    }
};

const isTokenValid = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        return payload.exp > now;
    } catch (error) {
        return false;
    }
};

// Anonymous Token Cache (shared across instances)
let anonymousTokenCache: TokenCache | null = null;
let hasLoggedAnonymousMode = false;

// Rate limiting for anonymous token requests
let lastTokenRequest = 0;
const minTokenInterval = 3000; // 3 seconds between token requests

// Anonymous token service with caching
const getAnonymousToken = async (tenantId: string): Promise<string | null> => {
    try {
        // Check if we have a valid cached token
        if (anonymousTokenCache &&
            anonymousTokenCache.tenantId === tenantId &&
            Date.now() < anonymousTokenCache.expiresAt - 60000) { // Refresh 1 minute before expiry
            console.log('🎟️ Using cached anonymous token');
            return anonymousTokenCache.token;
        }

        // Rate limiting check
        const now = Date.now();
        if (now - lastTokenRequest < minTokenInterval) {
            console.log('🚫 Anonymous token request skipped due to rate limiting');
            return anonymousTokenCache?.token || null;
        }
        lastTokenRequest = now;

        // 🎫 Use WebSocket Gateway instead of tenant-management (removes bottleneck)
        const apiPath = withApiBaseUrl('/api/v1/websocket-gateway/anonymous-token');

        console.log('🎟️ Requesting new anonymous token...', { tenantId, apiPath });

        // Use ApiRequestManager to queue the request with HIGH priority
        const data = await queueApiRequest(
            async () => {
                const response = await fetch(apiPath, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        tenantId: tenantId,
                        purpose: 'content_updates',
                        scope: ['websocket', 'firebase-config']
                    })
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                return await response.json();
            },
            REQUEST_PRIORITY.HIGH,
            `Anonymous Token (${tenantId})`
        );
        if (data.success && data.token) {
            console.log('✅ Anonymous token received from WebSocket Gateway', {
                tenantId: data.tenantId,
                expiresIn: data.expiresIn,
                scope: data.scope,
                sessionId: data.sessionId
            });

            // Cache the token
            anonymousTokenCache = {
                token: data.token,
                expiresAt: Date.now() + (data.expiresIn * 1000), // Convert to milliseconds
                tenantId: tenantId
            };

            return data.token;
        } else {
            console.error('❌ Anonymous token response error:', data.error);
            anonymousTokenCache = null;
            return null;
        }
    } catch (error) {
        console.error('❌ Failed to get anonymous token:', error);
        anonymousTokenCache = null;
        return null;
    }
};

export const useSmartWebSocket = ({
    tenantId,
    onContentUpdate,
    onConfigUpdate,
    enabled = true
}: UseSmartWebSocketProps) => {
    const socketRef = useRef<Socket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const presenceIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttempts = useRef(0);
    const maxReconnectAttempts = 3; // Reduced from 5 to 3
    const lastFailureTime = useRef<number>(0);
    const circuitBreakerThreshold = 30000; // 30 seconds
    const [isConnected, setIsConnected] = useState(false);
    const [authType, setAuthType] = useState<'anonymous' | 'authenticated' | null>(null);
    const currentTokenRef = useRef<string | null>(null);
    const authCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isConnectingRef = useRef<boolean>(false);
    const connectionIdRef = useRef<string>('');
    const tenantIdRef = useRef<string>(tenantId);
    const isKickedRef = useRef<boolean>(false); // Flag to prevent reconnection after being kicked

    // Update refs when props change
    useEffect(() => {
        tenantIdRef.current = tenantId;
    }, [tenantId]);

    const clearPresencePing = useCallback(() => {
        if (presenceIntervalRef.current) {
            clearInterval(presenceIntervalRef.current);
            presenceIntervalRef.current = null;
        }
    }, []);

    const emitPresencePing = useCallback((socket: Socket, connectionType: 'authenticated' | 'anonymous') => {
        if (connectionType !== 'authenticated' || !socket.connected) return;
        socket.emit('user_presence_ping', {
            tenantId: tenantIdRef.current,
            timestamp: new Date().toISOString()
        });
    }, []);

    const startPresencePing = useCallback((socket: Socket, connectionType: 'authenticated' | 'anonymous') => {
        clearPresencePing();
        if (connectionType !== 'authenticated') return;
        emitPresencePing(socket, connectionType);
        presenceIntervalRef.current = setInterval(() => {
            emitPresencePing(socket, connectionType);
        }, USER_PRESENCE_PING_MS);
    }, [clearPresencePing, emitPresencePing]);

    // Handle user kicked by admin
    const handleUserKicked = useCallback(async (reason: string, message: string) => {
        console.log('🚀 Handling user kick:', { reason, message });

        // Set kicked flag to prevent reconnection
        isKickedRef.current = true;

        // Clear any pending reconnection attempts
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        clearPresencePing();

        // Disable WebSocket to prevent reconnection
        reconnectAttempts.current = maxReconnectAttempts; // Set to max to prevent reconnection

        // Show SweetAlert2 notification
        await Swal.fire({
            icon: 'warning',
            title: '🚀 คุณถูกเตะออกจากระบบ',
            html: `
                <div style="text-align: center; padding: 20px;">
                    <div style="font-size: 18px; margin-bottom: 15px; color: #dc2626; font-weight: bold;">
                        <strong>ผู้ดูแลระบบได้เตะคุณออกจากระบบ</strong>
                    </div>
                    <div style="color: #dc2626; margin-bottom: 15px; white-space: pre-wrap; word-wrap: break-word; text-align: center; font-size: 16px; line-height: 1.6;">
                        ${message || 'คุณจะถูกนำกลับไปหน้าเข้าสู่ระบบ'}
                    </div>
                    <div style="color: #dc2626; font-size: 14px; font-weight: 600; text-align: center;">
                        กำลังออกจากระบบใน <span id="countdown" style="font-size: 18px; font-weight: bold;">5</span> วินาที...
                    </div>
                </div>
            `,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            timer: 5000,
            timerProgressBar: true,
            background: '#fff',
            customClass: {
                popup: 'kicked-popup',
                title: 'kicked-title',
                htmlContainer: 'kicked-content'
            },
            didOpen: () => {
                // Countdown animation
                let timeLeft = 5;
                const countdownElement = document.getElementById('countdown');

                const countdownTimer = setInterval(() => {
                    timeLeft--;
                    if (countdownElement) {
                        countdownElement.textContent = timeLeft.toString();
                    }

                    if (timeLeft <= 0) {
                        clearInterval(countdownTimer);
                    }
                }, 1000);
            }
        });

        // Disconnect WebSocket immediately (force disconnect, no reconnection)
        if (socketRef.current) {
            socketRef.current.removeAllListeners(); // Remove all listeners to prevent reconnection
            socketRef.current.disconnect(); // Force disconnect
            socketRef.current = null;
        }

        // Clear global socket reference
        if ((window as any).__huayWebSocket) {
            (window as any).__huayWebSocket = null;
        }

        // Dispatch custom event to notify chat WebSocket and other components
        window.dispatchEvent(new CustomEvent('user_kicked', {
            detail: { reason, message }
        }));

        // Clear local storage and reload page to force logout
        try {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('authToken');
            sessionStorage.clear();
            console.log('🚀 Cleared auth data after kick');
        } catch (error) {
            console.error('🚀 Error clearing auth data:', error);
        }

        // Force reload to login page
        window.location.href = '/login';
    }, [clearPresencePing]);

    // Handle balance adjustment notification
    const handleBalanceNotification = useCallback(async (data: {
        type: string;
        userId: string;
        username: string;
        adjustmentAmount: number;
        adjustmentType: 'increase' | 'decrease';
        adjustmentCategory: string;
        reason: string;
        newBalance: string | number;
        adjustedBy: string;
        timestamp: string;
        message: string;
    }) => {
        console.log('💰 Handling balance notification:', data);

        // Determine icon and colors based on adjustment type
        const isIncrease = data.adjustmentType === 'increase';
        const icon = isIncrease ? 'success' : 'info';
        const titleColor = isIncrease ? '#10b981' : '#f59e0b';
        const amountColor = isIncrease ? '#059669' : '#d97706';

        // Format amount for display
        const formattedAmount = Math.abs(data.adjustmentAmount).toLocaleString();

        // Category mapping to Thai
        const categoryMap: { [key: string]: string } = {
            'deposit_withdraw': 'ฝากเงิน/ถอนเงิน',
            'bonus': 'โบนัส',
            'other': 'อื่นๆ'
        };
        const categoryText = categoryMap[data.adjustmentCategory] || data.adjustmentCategory;

        // Show toast notification
        await Swal.fire({
            icon: icon,
            title: isIncrease ? '💰 ยอดเงินเพิ่มขึ้น' : '💳 ยอดเงินถูกหัก',
            html: `
                <div style="text-align: center; padding: 20px;">
                    <div style="font-size: 24px; margin-bottom: 15px; color: ${amountColor};">
                        <strong>${isIncrease ? '+' : '-'}${formattedAmount} บาท</strong>
                    </div>
                    <div style="color: #666; margin-bottom: 10px; font-size: 16px;">
                        <strong>ประเภท:</strong> ${categoryText}
                    </div>
                    ${data.newBalance !== 'unknown' ? `
                        <div style="color: #999; font-size: 14px; border-top: 1px solid #eee; padding-top: 10px;">
                            ยอดเงินปัจจุบัน: ${typeof data.newBalance === 'number' ? data.newBalance.toLocaleString() : data.newBalance} บาท
                        </div>
                    ` : ''}
                </div>
            `,
            showConfirmButton: true,
            confirmButtonText: 'รับทราบ',
            confirmButtonColor: titleColor,
            timer: 8000,
            timerProgressBar: true,
            background: '#fff',
            customClass: {
                popup: 'balance-notification-popup',
                confirmButton: 'balance-confirm-btn'
            }
        });
    }, []);

    const detectAuthStatus = useCallback((): {
        isAuthenticated: boolean;
        token: string | null;
        authType: 'authenticated' | 'anonymous'
    } => {
        const authToken = getAuthToken();

        if (authToken && isTokenValid(authToken)) {
            console.log('🔑 Detected valid authenticated token', {
                tokenPreview: authToken.substring(0, 20) + '...',
                tokenLength: authToken.length,
                tokenSource: localStorage.getItem('accessToken') ? 'accessToken' : 
                           localStorage.getItem('token') ? 'token' : 
                           localStorage.getItem('authToken') ? 'authToken' : 'unknown'
            });
            return { isAuthenticated: true, token: authToken, authType: 'authenticated' };
        }

        if ((import.meta.env as any).DEV && !hasLoggedAnonymousMode) {
            console.log('🎟️ No valid auth token, using anonymous mode', {
                hasAccessToken: !!localStorage.getItem('accessToken'),
                hasToken: !!localStorage.getItem('token'),
                hasAuthToken: !!localStorage.getItem('authToken'),
                accessTokenPreview: localStorage.getItem('accessToken')?.substring(0, 20) || null
            });
            hasLoggedAnonymousMode = true;
        }
        return { isAuthenticated: false, token: null, authType: 'anonymous' };
    }, []);

    const connect = useCallback(async (forceConnect = false) => {
        // Don't connect if user was kicked
        if (isKickedRef.current) {
            console.log('🚀 Connection blocked: User was kicked by admin');
            return;
        }

        const currentTenantId = tenantIdRef.current;

        if (!forceConnect && (!enabled || !currentTenantId)) {
            console.log('🔌 Connect skipped: disabled or no tenantId', { enabled, currentTenantId });
            return;
        }

        // For forced connections, ensure we have tenantId at minimum
        if (forceConnect && !currentTenantId) {
            console.log('🔌 Forced connect skipped: no tenantId', { currentTenantId });
            return;
        }

        console.log('🔌 Connect proceeding with:', { forceConnect, enabled, currentTenantId });

        // Prevent multiple simultaneous connections
        if (isConnectingRef.current) {
            console.log('🔌 Connect skipped: already connecting');
            return;
        }

        const connectionId = `${currentTenantId}-${Date.now()}`;
        connectionIdRef.current = connectionId;
        isConnectingRef.current = true;

        try {
            // Close existing connection if any
            if (socketRef.current) {
                console.log('🔌 Closing existing WebSocket connection');
                try {
                    socketRef.current.removeAllListeners();
                    socketRef.current.disconnect();
                    socketRef.current.close();
                } catch (e) {
                    console.warn('Error closing existing socket:', e);
                }
                socketRef.current = null;
            }

            // Check if this connection is still valid (prevent race conditions)
            if (connectionIdRef.current !== connectionId) {
                console.log('🔌 Connection cancelled: newer connection started');
                return;
            }

            const authStatus = detectAuthStatus();
            let token: string | null = null;
            let connectionType: 'authenticated' | 'anonymous';

            if (authStatus.isAuthenticated && authStatus.token) {
                // Use authenticated JWT token
                token = authStatus.token;
                connectionType = 'authenticated';
                console.log(`🔑 Connecting as authenticated user for tenant: ${currentTenantId}`);
            } else {
                // Get anonymous token
                token = await getAnonymousToken(currentTenantId);
                connectionType = 'anonymous';
                console.log(`🎟️ Connecting as anonymous user for tenant: ${currentTenantId}`);
            }

            if (!token) {
                console.error('❌ Failed to get token for WebSocket connection');
                setIsConnected(false);
                attemptReconnect();
                return;
            }

            currentTokenRef.current = token;
            setAuthType(connectionType);

            // Socket.IO connection
            const wsUrl = '/'; // Vite will proxy /socket.io to websocket-gateway:4104
            // Reduced connection logging
            if (process.env.NODE_ENV === 'development') {
                console.log(`🔌 Connecting WebSocket (${connectionType}) via proxy:`, wsUrl);
            }

            const socketConfig: any = {
                transports: ['websocket', 'polling'],
                upgrade: true,
                rememberUpgrade: false,
                query: {
                    tenantId: currentTenantId,
                    type: connectionType === 'authenticated' ? 'full_features' : 'content_updates',
                    token: token
                },
                timeout: 20000,
                reconnection: false, // We handle reconnection manually
                withCredentials: connectionType === 'authenticated',
            };

            if (connectionType === 'authenticated') {
                socketConfig.auth = { token: token };
            }

            const socket = io(wsUrl, socketConfig);

            // Connection success
            socket.on('connect', () => {
                // Check if this connection is still valid
                if (connectionIdRef.current !== connectionId) {
                    console.log('🔌 Connection success ignored: connection outdated');
                    socket.disconnect();
                    return;
                }

                console.log(`✅ WebSocket connected (${connectionType}) with token`, {
                    socketId: socket.id,
                    connected: socket.connected,
                    tenantId: currentTenantId,
                    hasToken: !!token,
                    transport: socket.io?.engine?.transport?.name || 'unknown',
                    query: (socket as any).handshake?.query || {},
                    authType: connectionType,
                    tokenPreview: token ? `${token.substring(0, 20)}...` : null
                });

                // Log user info if authenticated
                if (connectionType === 'authenticated' && token) {
                    try {
                        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                        console.log('🔑 [WS DEBUG] Authenticated user info from token:', {
                            userId: tokenPayload.sub || tokenPayload.userId || tokenPayload.id,
                            username: tokenPayload.username,
                            tenantId: tokenPayload.tenantId,
                            role: tokenPayload.role
                        });
                    } catch (e) {
                        console.warn('⚠️ Could not parse token payload:', e);
                    }
                }

                // Store socket instance globally for Firebase config access
                (window as any).__huayWebSocket = socket;

                setIsConnected(true);
                setAuthType(connectionType);
                isConnectingRef.current = false; // Reset connecting flag

                // Reset all reconnection state on successful connection
                reconnectAttempts.current = 0;
                lastFailureTime.current = 0;

                // Clear any pending reconnection
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }

                startPresencePing(socket, connectionType);

                socket.emit('join-channel', {
                    channel: `content_updates_${currentTenantId}`,
                    userInfo: {
                        tenantId: currentTenantId,
                        authType: connectionType,
                    },
                });
            });

            // Auto-joined channel confirmation
            socket.on('channel_joined', (data) => {
                // Reduced logging for channel joins
                if (process.env.NODE_ENV === 'development') {
                    console.log('📡 Auto-joined channel:', data.channel);
                }
            });

            // Content update handler (legacy shape: { type, content } only)
            // When data has tenantId, the handler below does tenant check and calls onContentUpdate once — avoid duplicate refresh
            socket.on('content_update', (data: ContentUpdateData) => {
                if (data.type === 'content_update' && data.content && !(data as any).tenantId) {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('🔄 Content updated via WebSocket');
                    }
                    onContentUpdate(data.content);
                }
            });

            // Configuration update handler (Real-time SMS config updates)
            socket.on('config_updated', (data: ConfigUpdateData) => {
                console.log('⚙️ Configuration updated via WebSocket:', {
                    type: data.type,
                    configType: data.configType,
                    updateType: data.metadata?.updateType,
                    affectedProviders: data.metadata?.affectedProviders,
                    updatedBy: data.metadata?.updatedBy,
                    broadcast: data.broadcast,
                    tenantId: data.tenantId
                });

                // 🧪 DEBUG: Log WebSocket config updates (removed toast to avoid UI clutter)
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔔 WebSocket Config Update Details:', {
                        type: data.type,
                        configType: data.configType,
                        updateType: data.metadata?.updateType,
                        tenantId: data.tenantId,
                        updatedBy: data.metadata?.updatedBy,
                        broadcast: data.broadcast
                    });
                }

                // Handle both CONFIG_UPDATE and SMS_CONFIG_UPDATE types
                if (onConfigUpdate && (data.type === 'CONFIG_UPDATE' || data.type === 'SMS_CONFIG_UPDATE')) {
                    const configType = data.configType || 'sms_provider';
                    onConfigUpdate(configType, data.data, data.metadata);
                }
            });

            // Kicked event handler
            socket.on('kicked', (data: { reason: string; message: string; timestamp: string }) => {
                console.log('🚀 User kicked by admin:', data);
                // Use the custom message from admin, or fallback to default
                const displayMessage = data.message || 'คุณถูกเตะออกจากระบบโดยผู้ดูแลระบบ';
                handleUserKicked(data.reason, displayMessage);
            });

            // Balance adjustment notification handler
            socket.on('balance_notification', (data: {
                type: string;
                userId: string;
                username: string;
                adjustmentAmount: number;
                adjustmentType: 'increase' | 'decrease';
                adjustmentCategory: string;
                reason: string;
                newBalance: string | number;
                adjustedBy: string;
                timestamp: string;
                message: string;
            }) => {
                console.log('💰 Balance adjustment notification received:', data);
                // Dispatch first so WalletContext can refresh balance immediately (before user closes Swal)
                const balanceEvent = new CustomEvent('balance_notification', { detail: data });
                window.dispatchEvent(balanceEvent);
                // Then show toast (Swal) — don't await so UI balance updates right away
                handleBalanceNotification(data);
            });

            socket.on('maintenance_mode_changed', (data: MaintenanceSocketEvent) => {
                const currentTenant = tenantIdRef.current;
                if (data.tenantId !== null && data.tenantId !== undefined && data.tenantId !== currentTenant) {
                    return;
                }

                window.dispatchEvent(new CustomEvent('maintenance_mode_changed', {
                    detail: {
                        tenantId: data.tenantId ?? currentTenant,
                        enabled: data.enabled === true,
                        reason: data.reason,
                        estimatedEnd: data.estimatedEnd ?? null,
                        maintenanceStart: data.maintenanceStart ?? null
                    }
                }));
            });

            socket.on('maintenance_warning', (data: MaintenanceSocketEvent) => {
                const currentTenant = tenantIdRef.current;
                if (data.tenantId !== null && data.tenantId !== undefined && data.tenantId !== currentTenant) {
                    return;
                }

                window.dispatchEvent(new CustomEvent('maintenance_warning', {
                    detail: {
                        tenantId: data.tenantId ?? currentTenant,
                        reason: data.reason,
                        estimatedEnd: data.estimatedEnd ?? null,
                        maintenanceStart: data.maintenanceStart ?? null,
                        countdownMinutes: data.countdownMinutes ?? 5
                    }
                }));
            });

            // Content update notification handler (from WebSocket Gateway)
            socket.on('content_update', (data: {
                type: string;
                tenantId?: string;
                content: any;
                contentType?: string;
                updatedBy?: string;
                timestamp?: string;
            }) => {
                console.log('🔄 Content update notification received:', data);

                // tenantId อาจอยู่ที่ data.tenantId หรือ content/round (บาง gateway)
                const msgTenantId = data.tenantId ?? data.content?.tenantId;
                const roundData = data.content?.round || data.content?.data || data.content;
                const roundTenantId = roundData?.tenantId;
                const currentTenant = tenantIdRef.current;
                const isForCurrentTenant = currentTenant && (
                    msgTenantId === currentTenant ||
                    roundTenantId === currentTenant
                );
                if (!isForCurrentTenant) {
                    if (process.env.NODE_ENV === 'development' && data.contentType === 'lottery_rounds') {
                        console.log('⏭️ Skipping lottery_rounds: tenant mismatch', {
                            msgTenantId,
                            roundTenantId,
                            currentTenant
                        });
                    }
                    return;
                }
                console.log('✅ Content update for current tenant, refreshing...');

                // ประกาศ: ส่งทันที — ให้ refetch และแสดงอีกครั้ง (ลบ acked แล้วโหลดใหม่)
                if (data.contentType === 'announcement_send_now') {
                    if (process.env.NODE_ENV === 'development') {
                        console.log('📢 Announcement send-now received, dispatching refetch event');
                    }
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('announcement_refetch', { detail: data.content }));
                    }, 0);
                    return;
                }

                // Handle deposit account specific updates
                    if (data.contentType === 'deposit_accounts') {
                        console.log('🏦 Deposit account update received:', data.content);

                        // Dispatch custom event for deposit account updates
                        const depositAccountEvent = new CustomEvent('deposit_account_websocket_event', {
                            detail: {
                                type: data.content.type,
                                action: data.content.action,
                                account: data.content.account,
                                newStatus: data.content.newStatus,
                                timestamp: data.timestamp
                            }
                        });
                        window.dispatchEvent(depositAccountEvent);

                        const depositAccountsUpdatedEvent = new CustomEvent('deposit_accounts_updated', {
                            detail: {
                                tenantId: data.tenantId,
                                content: data.content,
                                timestamp: data.timestamp
                            }
                        });
                        window.dispatchEvent(depositAccountsUpdatedEvent);

                        // Also trigger general content update for compatibility
                        if (onContentUpdate && data.content) {
                            console.log('📦 Using content from WebSocket notification:', data.content);
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'withdrawal_settings') {
                        const depositChannelsEvent = new CustomEvent('deposit_channels_updated', {
                            detail: {
                                tenantId: data.tenantId,
                                content: data.content,
                                timestamp: data.timestamp
                            }
                        });
                        window.dispatchEvent(depositChannelsEvent);

                        if (onContentUpdate && data.content) {
                            console.log('📦 Using content from WebSocket notification:', data.content);
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'betting_packages' || data.contentType === 'betting-packages') {
                        // Handle betting package updates
                        console.log('📦 Betting package update received:', data.content);
                        
                        // Dispatch custom event for betting package updates
                        const packageUpdateEvent = new CustomEvent('betting_package_updated', {
                            detail: {
                                type: data.content?.type || 'package_updated',
                                action: data.content?.action || 'update',
                                package: data.content?.package,
                                packages: data.content?.packages,
                                tenantId: data.tenantId,
                                timestamp: data.timestamp
                            }
                        });
                        window.dispatchEvent(packageUpdateEvent);
                        
                        // Also trigger general content update for compatibility
                        if (onContentUpdate && data.content) {
                            console.log('📦 Using content from WebSocket notification:', data.content);
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'lottery_rounds') {
                        // รอบหวย status ถูกแก้จาก admin - แจ้ง /lotto อัปเดตรายการ, /lottery-result อัปเดตผล
                        const roundData = data.content?.round || data.content?.data || data.content;
                        if (roundData) {
                            const roundUpdateEvent = new CustomEvent('lottery_round_updated', {
                                detail: {
                                    type: data.content?.type || 'lottery_round_update',
                                    round: roundData,
                                    contentType: 'lottery_rounds',
                                    tenantId: data.tenantId,
                                    timestamp: data.timestamp
                                }
                            });
                            window.dispatchEvent(roundUpdateEvent);
                        }
                        if (onContentUpdate && data.content) {
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'huay_type_updated') {
                        // เปิด/ปิด ประเภทหวยจาก admin - หน้า lottery-result / LottoPage จะ refetch และซ่อน/แสดง section
                        const huayTypeData = data.content?.huayType || data.content;
                        if (huayTypeData) {
                            const isActive = data.content?.isActive ?? huayTypeData?.configuration?.isActive;
                            const name = data.content?.name ?? huayTypeData?.name;
                            const huayTypeEvent = new CustomEvent('huay_type_updated', {
                                detail: {
                                    type: 'huay_type_updated',
                                    huayType: huayTypeData,
                                    code: data.content?.code ?? huayTypeData?.code,
                                    isActive,
                                    name,
                                    tenantId: data.tenantId,
                                    timestamp: data.timestamp
                                }
                            });
                            window.dispatchEvent(huayTypeEvent);
                        }
                        if (onContentUpdate && data.content) {
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'prediction_events_updated') {
                        window.dispatchEvent(new CustomEvent('prediction_events_updated', {
                            detail: {
                                ...(data.content || {}),
                                tenantId: data.tenantId,
                                timestamp: data.timestamp
                            }
                        }));
                        if (onContentUpdate && data.content) {
                            onContentUpdate(data.content);
                        }
                    } else if (data.contentType === 'user_service_access') {
                        window.dispatchEvent(new CustomEvent('user_service_access_updated', {
                            detail: {
                                ...(data.content || {}),
                                tenantId: data.tenantId,
                                timestamp: data.timestamp
                            }
                        }));

                        if (onContentUpdate && data.content) {
                            console.log('📦 Using content from WebSocket notification:', data.content);
                            onContentUpdate(data.content);
                        }
                    } else {
                        // Use the actual content data from WebSocket for other content types
                        if (onContentUpdate && data.content) {
                            console.log('📦 Using content from WebSocket notification:', data.content);
                            onContentUpdate(data.content);
                        }
                    }
            });

            socket.on('affiliate_config_updated', (data: {
                tenantId?: string;
                enabled?: boolean;
                updatedBy?: string;
                timestamp?: string;
                broadcastedAt?: string;
            }) => {
                const currentTenant = tenantIdRef.current;
                if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
                    return;
                }

                window.dispatchEvent(new CustomEvent('affiliate_config_updated', {
                    detail: {
                        tenantId: data?.tenantId || currentTenant,
                        enabled: data?.enabled === true,
                        updatedBy: data?.updatedBy,
                        timestamp: data?.timestamp || data?.broadcastedAt
                    }
                }));
            });

            socket.on('user_service_access_updated', (data: {
                tenantId?: string;
                [key: string]: any;
            }) => {
                const currentTenant = tenantIdRef.current;
                if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
                    return;
                }

                window.dispatchEvent(new CustomEvent('user_service_access_updated', {
                    detail: {
                        ...data,
                        tenantId: data?.tenantId || currentTenant
                    }
                }));
            });

            socket.on('game_control_updated', (data: {
                tenantId?: string;
                [key: string]: any;
            }) => {
                const currentTenant = tenantIdRef.current;
                if (data?.tenantId && currentTenant && data.tenantId !== currentTenant) {
                    return;
                }

                window.dispatchEvent(new CustomEvent('game_control_updated', {
                    detail: {
                        ...data,
                        tenantId: data?.tenantId || currentTenant
                    }
                }));
            });

            // 🎪 Popup management event handlers
            socket.on('popup_show', (data: {
                tenantId: string;
                popup: any;
                forcedBy: string;
                timestamp: string;
            }) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('🎪 Popup show event received:', data);
                }

                // Dispatch custom event for popup manager
                const popupEvent = new CustomEvent('popup_websocket_event', {
                    detail: {
                        type: 'popup_show',
                        data: data
                    }
                });
                window.dispatchEvent(popupEvent);
            });

            socket.on('popup_system_toggle', (data: {
                tenantId: string;
                enabled: boolean;
                toggledBy: string;
                timestamp: string;
            }) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('🎪 Popup system toggle event received:', data);
                }

                // Dispatch custom event for popup manager
                const popupEvent = new CustomEvent('popup_websocket_event', {
                    detail: {
                        type: 'popup_system_toggle',
                        data: data
                    }
                });
                window.dispatchEvent(popupEvent);
            });

            // 🎯 Admin popup system toggle event (from admin panel)
            socket.on('admin_popup_system_toggle', (data: {
                tenantId: string;
                enabled: boolean;
                toggledBy: string;
                timestamp: string;
            }) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('👨‍💼 Admin popup system toggle event received via WebSocket:', data);
                }

                if (data.tenantId === currentTenantId) {
                    // Dispatch as admin_popup_system_toggle to match popup manager listener
                    const popupEvent = new CustomEvent('popup_websocket_event', {
                        detail: {
                            type: 'admin_popup_system_toggle',
                            data: data
                        }
                    });

                    window.dispatchEvent(popupEvent);

                    console.log(`🔄 Admin toggled popup system: ${data.enabled ? 'ENABLED' : 'DISABLED'}`);
                }
            });

            socket.on('popup_clear_sessions', (data: {
                tenantId: string;
                clearedBy: string;
                timestamp: string;
            }) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('🎪 Popup clear sessions event received:', data);
                }

                // Dispatch custom event for popup manager
                const popupEvent = new CustomEvent('popup_websocket_event', {
                    detail: {
                        type: 'popup_clear_sessions',
                        data: data
                    }
                });
                window.dispatchEvent(popupEvent);
            });

            socket.on('popup_refresh', (data: {
                tenantId: string;
                refreshedBy: string;
                timestamp: string;
            }) => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('🎪 Popup refresh event received:', data);
                }

                // Dispatch custom event for popup manager
                const popupEvent = new CustomEvent('popup_websocket_event', {
                    detail: {
                        type: 'popup_refresh',
                        data: data
                    }
                });
                window.dispatchEvent(popupEvent);
            });

            // Authentication error handler
            socket.on('auth_error', (error) => {
                if (process.env.NODE_ENV === 'development') {
                    console.error('🔒 WebSocket authentication error:', error);
                }
                setIsConnected(false);
                if (connectionType === 'authenticated') {
                    // Auth token might be expired, clear cache and try anonymous
                    anonymousTokenCache = null; // Clear anonymous cache too
                    currentTokenRef.current = null;
                    attemptReconnect();
                } else {
                    // Anonymous token failed, clear and retry
                    anonymousTokenCache = null;
                    attemptReconnect();
                }
            });

            // CORS error handler
            socket.on('cors_error', (error) => {
                if (process.env.NODE_ENV === 'development') {
                    console.error('🌍 WebSocket CORS error:', error);
                }
                setIsConnected(false);
                attemptReconnect();
            });

            // Disconnection handler
            socket.on('disconnect', (reason) => {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('🔌 WebSocket disconnected:', reason);
                }

                // Clear global socket reference
                if ((window as any).__huayWebSocket === socket) {
                    (window as any).__huayWebSocket = null;
                }

                clearPresencePing();

                setIsConnected(false);

                // Don't reconnect if user was kicked
                if (isKickedRef.current) {
                    console.log('🚀 User was kicked, preventing reconnection');
                    return;
                }

                // Auto-reconnect unless manually disconnected
                if (reason !== 'io client disconnect') {
                    attemptReconnect();
                }
            });

            // Connection error handler
            socket.on('connect_error', (error) => {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('🔌 WebSocket connection error:', error);
                }
                setIsConnected(false);
                
                // Don't reconnect if user was kicked
                if (!isKickedRef.current) {
                    attemptReconnect();
                } else {
                    console.log('🚀 Connection error after kick, preventing reconnection');
                }
            });

            socketRef.current = socket;
        } catch (error) {
            console.error('Failed to establish WebSocket connection:', error);
            isConnectingRef.current = false; // Reset connecting flag on error
            setIsConnected(false);
            
            // Don't reconnect if user was kicked
            if (!isKickedRef.current) {
                attemptReconnect();
            } else {
                console.log('🚀 Connection failed after kick, preventing reconnection');
            }
        } finally {
            // Ensure connecting flag is reset
            if (connectionIdRef.current === connectionId) {
                isConnectingRef.current = false;
            }
        }
    }, [enabled, detectAuthStatus, onContentUpdate, startPresencePing, clearPresencePing]); // Removed tenantId dependency

    const attemptReconnect = useCallback(() => {
        // Don't reconnect if user was kicked
        if (isKickedRef.current) {
            console.log('🚀 Reconnection blocked: User was kicked by admin');
            return;
        }

        if (!enabled) {
            console.log('🔌 Reconnection skipped: WebSocket disabled');
            return;
        }

        // Circuit breaker: don't reconnect too soon after last failure
        const now = Date.now();
        if (now - lastFailureTime.current < circuitBreakerThreshold) {
            const waitTime = circuitBreakerThreshold - (now - lastFailureTime.current);
            console.log(`🔌 Circuit breaker active: waiting ${Math.round(waitTime / 1000)}s before next attempt`);

            reconnectTimeoutRef.current = setTimeout(() => {
                attemptReconnect();
            }, waitTime);
            return;
        }

        if (reconnectAttempts.current < maxReconnectAttempts) {
            // More conservative exponential backoff for user frontend
            const baseDelay = 5000; // Start with 5 seconds
            const delay = Math.min(baseDelay * Math.pow(2, reconnectAttempts.current), 120000); // Max 2 minutes
            console.log(`🔌 Reconnecting WebSocket in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);

            reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttempts.current++;
                lastFailureTime.current = Date.now();
                connect();
            }, delay);
        } else {
            console.error('🔌 Max WebSocket reconnection attempts reached - stopping reconnections for 5 minutes');
            setIsConnected(false);
            setAuthType(null);

            // Reset after 5 minutes
            setTimeout(() => {
                reconnectAttempts.current = 0;
                console.log('🔌 Reconnection attempts reset, you can try again');
            }, 300000); // 5 minutes
        }
    }, [connect, enabled, circuitBreakerThreshold]);

    const disconnect = useCallback(() => {
        console.log('🔌 Forcefully disconnecting WebSocket...');
        clearPresencePing();

        // Force close socket connection
        if (socketRef.current) {
            try {
                socketRef.current.removeAllListeners(); // Remove all event listeners
                socketRef.current.disconnect();
                socketRef.current.close(); // Force close
            } catch (error) {
                console.warn('Error closing socket:', error);
            }
            socketRef.current = null;
        }

        // Clear all timers
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (authCheckIntervalRef.current) {
            clearInterval(authCheckIntervalRef.current);
            authCheckIntervalRef.current = null;
        }

        // Reset all state
        currentTokenRef.current = null;
        reconnectAttempts.current = 0; // Reset attempts
        lastFailureTime.current = 0; // Reset failure time
        isConnectingRef.current = false; // Reset connecting flag
        connectionIdRef.current = ''; // Clear connection ID
        isKickedRef.current = false; // Reset kicked flag
        setIsConnected(false);
        setAuthType(null);

        console.log('🔌 WebSocket fully disconnected and cleaned up');
    }, [clearPresencePing]);

    const upgradeConnection = useCallback(() => {
        console.log('🔄 Upgrading WebSocket connection...');
        connect(true); // Force connect for upgrade
    }, [connect]);

    // Initial connection - STABLE dependencies to prevent loops
    useEffect(() => {
        if (enabled && tenantIdRef.current && !isConnectingRef.current && !socketRef.current) {
            console.log('🚀 Initial WebSocket connection for tenant:', tenantIdRef.current);
            connect();
        }

        return () => {
            // Only disconnect on unmount, not on every re-render
            return disconnect();
        };
    }, [enabled]); // Only enabled as dependency, using tenantIdRef

    // Periodic auth status check (every 30 seconds) - REDUCED frequency
    useEffect(() => {
        if (!enabled || !isConnected) return; // Only check when connected

        authCheckIntervalRef.current = setInterval(() => {
            const authStatus = detectAuthStatus();
            const currentIsAuth = authType === 'authenticated';
            const shouldBeAuth = authStatus.isAuthenticated;

            if (currentIsAuth !== shouldBeAuth && !isConnectingRef.current) {
                console.log(`🔄 Auth status changed: ${currentIsAuth ? 'authenticated' : 'anonymous'} → ${shouldBeAuth ? 'authenticated' : 'anonymous'}`);
                upgradeConnection();
            }
        }, 60000); // Increased to 60 seconds to reduce frequency

        return () => {
            if (authCheckIntervalRef.current) {
                clearInterval(authCheckIntervalRef.current);
            }
        };
    }, [enabled, authType, isConnected]); // Removed upgradeConnection from dependencies

    // Listen for storage changes (login/logout in other tabs) - DEBOUNCED
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        const handleStorageChange = (event: StorageEvent) => {
            if ((event.key === 'token' || event.key === 'authToken') && !isConnectingRef.current) {
                console.log('🔄 Auth token changed in localStorage, upgrading connection...');

                // Debounce to prevent multiple rapid calls
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    if (!isConnectingRef.current) {
                        upgradeConnection();
                    }
                }, 1000); // 1 second debounce
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []); // Empty dependency array

    // Custom auth change event listener (for same-tab login/logout) - DEBOUNCED
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        const handleAuthChange = () => {
            if (!isConnectingRef.current) {
                console.log('🔄 Auth change event received, upgrading connection...');

                // Debounce to prevent multiple rapid calls
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    if (!isConnectingRef.current) {
                        upgradeConnection();
                    }
                }, 1000); // 1 second debounce
            }
        };

        window.addEventListener('auth-change', handleAuthChange);
        return () => {
            window.removeEventListener('auth-change', handleAuthChange);
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []); // Empty dependency array

    return {
        isConnected,
        authType,
        reconnect: connect,
        disconnect,
        upgradeConnection
    };
};

/**
 * Get current WebSocket connection (for external use)
 * @returns {Socket | null} Current socket instance
 */
export const getWebSocketConnection = () => {
    // This is a simple implementation - in production you might want to use a global store
    return (window as any).__huayWebSocket || null;
};
